/** @jsx jsx */

import { Fragment as _, useContext, useEffect } from 'react';
import { css as _css } from 'emotion';
import { jsx } from '@emotion/core';
import setBlockProps from 'services/apiHelpers/setBlockProps';
import getBlockById from 'services/apiHelpers/getBlockById';
import { interactionContext } from './interactionContext';
import { Box } from 'theme-ui';
import { useSelector } from 'react-redux';
import { contentCard_default } from 'factories/interactions/contentcard';
import hexToRgb from 'services/hexToRGB';

const Text = props => {
  const { sx = {}, children } = props;

  return (
    <Box className="text" sx={sx}>
      {children}
    </Box>
  );
};

const ButtonWrapper = ({ children }) => {
  return (
    <Box
      className="btn"
      sx={{
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        margin: '15px 0 20px 0',
        padding: '0 10px',

        '> div': {
          width: '100%',
          button: {
            maxWidth: '100%',
          },
        },
        //
      }}
    >
      {children}
    </Box>
  );
};

const ContentCard = ({ children, ...props }) => {
  //

  let [media, text, btn] = children;

  // the order these are in from the factory :
  // Text
  // Image
  // Video
  // SVG
  // Embed
  // Audio
  // PDF
  const medias = ['text', 'image', 'video', 'svg', 'embed', 'audio', 'pdf'];
  const mediaIndex = media?.props?.child;

  const context = useContext(interactionContext) as any;

  const { id } = props as any;
  const block = getBlockById(id);

  // get the props directly off the block so updates happen
  const { selected, _props } = useSelector((state: any) => {
    return {
      selected: id === state.focusedNodeId,
      _props: block.props,
    };
  });

  const {
    includeCardBackground = true,
    contentCardBackgroundColor,
    contentcardBackgroundOpacity = 100,
    contentCardPanelTextColor,
    contentCardContentTextColor,
    width,
    height,
    borderRadius,
    outline = false,
    borderColor,
    imagePosition,
    includeText,
    includeButton = true,
    includeShadow,
    gridCell,
    className,
    uniformHeight = false,
  } = {
    ...(context.props as any),
    ...{
      //
      ..._props,
      ...(window.dev && window.dev() && selected && _props),
    },
  } as any;

  let mediaPadding: string | undefined;

  if (medias[mediaIndex]) {
    mediaPadding = {
      text: window.dev && window.dev() ? '10px' : '20px 20px 0 20px',
      pdf: window.dev && window.dev() ? '15px' : '0',
      undefined: '0',
    }[medias[mediaIndex]];
  }

  if (!mediaPadding) {
    mediaPadding = '0';
  }

  const textMargin = window.dev && window.dev() ? '10px' : '20px';

  const mediaBorderRadius =
    !includeCardBackground && !includeShadow
      ? {
          borderRadius,
          overflow: 'hidden',
        }
      : {};

  const { r, g, b } = hexToRgb(
    contentCardBackgroundColor === '#fff'
      ? '#ffffff'
      : contentCardBackgroundColor
  );
  const a = contentcardBackgroundOpacity / 100;

  return (
    <Box
      id={`card_${id}`}
      className={'card' || className}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',

        width: '100%',
        background: includeCardBackground
          ? `rgba(${r}, ${g}, ${b}, ${a})` || contentCardBackgroundColor
          : 'transparent',
        borderRadius,
        ...(includeShadow && {
          boxShadow: '0px 0px 10px rgba(13, 16, 40, 0.15)',
        }),

        ...(outline && {
          border: `1px solid ${borderColor}`,
        }),

        overflow: 'hidden',

        div: {
          color: contentCardContentTextColor,
        },

        '.video-player-container': {
          margin: 0,
        },
      }}
    >
      {imagePosition === 'top' && (
        <_>
          <span>
            <Box
              className="media"
              sx={{
                width: '100%',
                padding: mediaPadding,
                ...mediaBorderRadius,
              }}
            >
              {media}
            </Box>
            {includeText && (
              <Text
                sx={{
                  margin: textMargin,
                }}
              >
                {text}
              </Text>
            )}
          </span>
          {includeButton && <ButtonWrapper>{btn}</ButtonWrapper>}
        </_>
      )}

      {imagePosition === 'right' && (
        <Box>
          <Box sx={{ display: 'flex' }}>
            {includeText && (
              <Text sx={{ flex: 1, margin: textMargin }}>{text}</Text>
            )}
            <Box
              className="media"
              sx={{
                flex: 1,
                padding: mediaPadding,
                ...mediaBorderRadius,
              }}
            >
              {media}
            </Box>
          </Box>
          {includeButton && <ButtonWrapper>{btn}</ButtonWrapper>}
        </Box>
      )}

      {imagePosition === 'bottom' && (
        <_>
          <span>
            {includeText && <Text sx={{ margin: textMargin }}>{text}</Text>}
            <Box
              className="media"
              sx={{
                width: '100%',
                padding: mediaPadding,
                ...mediaBorderRadius,
              }}
            >
              {media}
            </Box>
          </span>
          {includeButton && <ButtonWrapper>{btn}</ButtonWrapper>}
        </_>
      )}

      {imagePosition === 'left' && (
        <Box>
          <Box sx={{ display: 'flex' }}>
            <Box
              className="media"
              sx={{
                flex: 1,
                padding: mediaPadding,
                ...mediaBorderRadius,
              }}
            >
              {media}
            </Box>
            {includeText && (
              <Text sx={{ flex: 1, margin: textMargin }}>{text}</Text>
            )}
          </Box>
          {includeButton && <ButtonWrapper>{btn}</ButtonWrapper>}
        </Box>
      )}

      {imagePosition === 'none' && (
        <Box>
          <Text sx={{ margin: `20px ${textMargin}` }}>{text}</Text>
          {includeButton && <ButtonWrapper>{btn}</ButtonWrapper>}
        </Box>
      )}
    </Box>
  );
};

export default ContentCard;
